import { useState } from "react";
import {
  montarMensagem,
  previaModelo,
  salvarModelo,
  limparModelo,
  abrirWhats,
  canalPreferido,
  salvarCanal,
} from "../services/mensagens";

// ---------------------------------------------------------------------------
// PAINEL DE ENVIO PELO WHATSAPP -- UM COMPONENTE, TODOS OS EVENTOS
//
// Existe para que Agendamentos.js e OS.js NÃO tenham cada um a sua cópia
// desta tela. Duas cópias divergem na primeira alteração -- foi exatamente
// assim que o limite de fotos do checklist acabou valendo 6 na tela e 3 no
// servidor. Aqui a regra é uma só, num lugar só.
//
// Quem chama diz apenas QUAL EVENTO é e QUAIS DADOS ele tem. O texto, as
// variáveis, o modelo próprio da oficina e o canal saem todos de
// services/mensagens.js.
//
// O componente não envia nada: ele prepara a mensagem e entrega ao WhatsApp
// do dono da oficina, que confere e aperta enviar. Por isso não existe aqui
// nenhum indicador de "entregue" -- seria um dado que não possuímos.
// ---------------------------------------------------------------------------

const VERDE = "#25d366";

export default function PainelWhatsApp({
  tipo,
  dados,
  oficina,
  tenantId,
  chave,
  titulo,
  subtitulo,
  t,
  aoEnviar,
}) {
  const tr = typeof t === "function" ? t : (k, p) => p || k;

  // O canal é preferência da oficina, não do registro: sobrevive à troca.
  const [canal, setCanal] = useState(() => canalPreferido(tenantId));
  const [versao, setVersao] = useState(0); // força recalcular após salvar

  // ── ESTADO AMARRADO AO REGISTRO ──────────────────────────────────────────
  // Tudo que é rascunho tem de morrer quando muda a O.S./agendamento aberto.
  // Sem isto o componente mantém a mesma instância ao trocar de registro e o
  // texto ajustado para um cliente apareceria -- e seria ENVIADO -- para o
  // seguinte. Comparar a chave durante o render zera sozinho, sem useEffect
  // e sem depender de quem chama lembrar de passar key.
  const chaveAtual = String(chave == null ? "" : chave) + "|" + tipo;
  const VAZIO = {
    chave: chaveAtual,
    verPrevia: false,
    textoEditado: null,
    editandoModelo: false,
    rascunhoModelo: "",
    aviso: "",
  };
  const [estado, setEstado] = useState(VAZIO);
  const est = estado.chave === chaveAtual ? estado : VAZIO;
  const mudar = (campos) => setEstado({ ...est, ...campos });

  const verPrevia = est.verPrevia;
  const textoEditado = est.textoEditado;
  const editandoModelo = est.editandoModelo;
  const rascunhoModelo = est.rascunhoModelo;
  const aviso = est.aviso;
  const setVerPrevia = (f) => mudar({ verPrevia: typeof f === "function" ? f(verPrevia) : f });
  const setTextoEditado = (v) => mudar({ textoEditado: v });
  const setEditandoModelo = (v) => mudar({ editandoModelo: v });
  const setRascunhoModelo = (f) => mudar({ rascunhoModelo: typeof f === "function" ? f(rascunhoModelo) : f });

  let m;
  try {
    m = montarMensagem(tipo, dados, oficina, tenantId);
  } catch (e) {
    // Tipo desconhecido é erro de programação, não do usuário: não vale
    // quebrar a tela inteira por causa de um painel.
    return null;
  }
  // "versao" entra aqui só para o React recalcular depois de salvar/limpar.
  void versao;

  const textoFinal = textoEditado != null ? textoEditado : m.texto;
  const semNumero = !m.destinatario;

  const trocarCanal = () => {
    const novo = canal === "web" ? "app" : "web";
    setCanal(salvarCanal(tenantId, novo));
  };

  const enviar = () => {
    if (!m.destinatario) return;
    abrirWhats(m.destinatario, textoFinal, canal);
    if (typeof aoEnviar === "function") aoEnviar(tipo, textoFinal);
  };

  const abrirEditorModelo = () => {
    mudar({ rascunhoModelo: m.temModelo ? m.modelo : m.textoPadrao, editandoModelo: true, aviso: "" });
  };

  const salvar = () => {
    salvarModelo(tenantId, tipo, rascunhoModelo);
    mudar({
      editandoModelo: false,
      textoEditado: null,
      aviso: tr("whats.modelo_salvo", "Modelo salvo. Vale para esta oficina, neste computador."),
    });
    setVersao((v) => v + 1);
  };

  const restaurar = () => {
    limparModelo(tenantId, tipo);
    mudar({
      editandoModelo: false,
      textoEditado: null,
      aviso: tr("whats.modelo_restaurado", "Texto padrão restaurado."),
    });
    setVersao((v) => v + 1);
  };

  const inserirVariavel = (nome) => {
    setRascunhoModelo((r) => r + "{" + nome + "}");
  };

  // ── sem número utilizável: explica em vez de oferecer um botão morto ─────
  if (semNumero) {
    return (
      <div
        style={{
          background: "var(--bg3)",
          border: "1px solid var(--border)",
          borderRadius: 10,
          color: "var(--text3)",
          fontSize: 12.5,
          marginBottom: 12,
          padding: "11px 14px",
        }}
      >
        {tr(
          "whats.sem_numero",
          "Para enviar pelo WhatsApp, o cadastro precisa de um telefone com DDD — 10 ou 11 dígitos."
        )}
      </div>
    );
  }

  const linkBotao = {
    background: "transparent",
    border: "none",
    color: "var(--text3)",
    cursor: "pointer",
    fontSize: 11.5,
    padding: 0,
    textDecoration: "underline",
  };

  const caixaTexto = {
    background: "var(--bg2)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text2)",
    fontFamily: "inherit",
    fontSize: 12.5,
    lineHeight: 1.55,
    margin: "8px 0 0",
    padding: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    width: "100%",
  };

  return (
    <div
      style={{
        background: VERDE + "12",
        border: "1px solid " + VERDE + "55",
        borderRadius: 10,
        marginBottom: 12,
        padding: "12px 14px",
      }}
    >
      <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "space-between" }}>
        <div>
          <div
            style={{
              color: VERDE,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
            }}
          >
            {titulo || m.rotulo}
          </div>
          <div style={{ color: "var(--text2)", fontSize: 12.5, marginTop: 3 }}>
            {subtitulo ||
              tr("whats.sub", "Abre o WhatsApp com a mensagem pronta. Você confere e envia.")}
          </div>
        </div>
        <button
          onClick={enviar}
          style={{
            background: VERDE,
            border: "none",
            borderRadius: 8,
            color: "#06281a",
            cursor: "pointer",
            fontSize: 12.5,
            fontWeight: 800,
            padding: "9px 16px",
          }}
        >
          {canal === "app"
            ? tr("whats.abrir_app", "Abrir no aplicativo")
            : tr("whats.abrir_web", "Abrir no WhatsApp Web")}
        </button>
      </div>

      <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8 }}>
        <button onClick={() => setVerPrevia((v) => !v)} style={linkBotao}>
          {verPrevia
            ? tr("whats.ocultar", "ocultar mensagem")
            : tr("whats.ver", "ver a mensagem antes")}
        </button>
        <button onClick={trocarCanal} style={linkBotao}>
          {canal === "app"
            ? tr("whats.usar_web", "prefiro abrir no WhatsApp Web")
            : tr("whats.usar_app", "prefiro abrir no aplicativo")}
        </button>
        <button onClick={abrirEditorModelo} style={linkBotao}>
          {m.temModelo
            ? tr("whats.editar_meu_modelo", "editar meu modelo")
            : tr("whats.criar_meu_modelo", "escrever meu próprio texto")}
        </button>
        {m.temModelo && (
          <button onClick={restaurar} style={{ ...linkBotao, color: "var(--text3)" }}>
            {tr("whats.restaurar", "voltar ao texto padrão")}
          </button>
        )}
      </div>

      {aviso && (
        <div style={{ color: VERDE, fontSize: 11.5, marginTop: 8 }}>{aviso}</div>
      )}

      {/* ── prévia + edição pontual ─────────────────────────────────────── */}
      {verPrevia && !editandoModelo && (
        <div>
          <textarea
            value={textoFinal}
            onChange={(e) => setTextoEditado(e.target.value)}
            rows={Math.min(18, textoFinal.split("\n").length + 1)}
            style={{ ...caixaTexto, resize: "vertical" }}
          />
          <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6 }}>
            <span style={{ color: "var(--text3)", fontSize: 11 }}>
              {tr("whats.edicao_pontual", "Você pode ajustar o texto acima — vale só para este envio.")}
            </span>
            {textoEditado != null && (
              <button onClick={() => setTextoEditado(null)} style={linkBotao}>
                {tr("whats.desfazer", "desfazer alterações")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── editor do modelo da oficina ─────────────────────────────────── */}
      {editandoModelo && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: "var(--text2)", fontSize: 12, marginBottom: 6 }}>
            {tr(
              "whats.modelo_ajuda",
              "Escreva o texto do jeito da sua oficina. Troque o que muda de cliente para cliente pelas etiquetas abaixo — elas são preenchidas sozinhas em cada envio."
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {(m.variaveisAceitas || []).map((v) => (
              <button
                key={v}
                onClick={() => inserirVariavel(v)}
                title={tr("whats.inserir", "inserir")}
                style={{
                  background: "var(--bg3)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  color: "var(--text2)",
                  cursor: "pointer",
                  fontFamily: "monospace",
                  fontSize: 11,
                  padding: "3px 8px",
                }}
              >
                {"{" + v + "}"}
              </button>
            ))}
          </div>

          <textarea
            value={rascunhoModelo}
            onChange={(e) => setRascunhoModelo(e.target.value)}
            rows={12}
            style={{ ...caixaTexto, margin: 0, resize: "vertical" }}
          />

          <div style={{ color: "var(--text3)", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", marginTop: 10, textTransform: "uppercase" }}>
            {tr("whats.previa_modelo", "Como vai ficar")}
          </div>
          <pre style={{ ...caixaTexto, margin: "4px 0 0" }}>
            {previaModelo(rascunhoModelo, dados, oficina)}
          </pre>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <button
              onClick={salvar}
              style={{
                background: VERDE,
                border: "none",
                borderRadius: 8,
                color: "#06281a",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                padding: "7px 14px",
              }}
            >
              {tr("whats.salvar_modelo", "Salvar como meu padrão")}
            </button>
            <button
              onClick={() => setEditandoModelo(false)}
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: 8,
                color: "var(--text2)",
                cursor: "pointer",
                fontSize: 12,
                padding: "7px 14px",
              }}
            >
              {tr("common.cancelar", "Cancelar")}
            </button>
          </div>

          <div style={{ color: "var(--text3)", fontSize: 11, marginTop: 8 }}>
            {tr(
              "whats.modelo_escopo",
              "O modelo fica guardado neste computador, para esta oficina. Em outro aparelho, volta o texto padrão."
            )}
          </div>
        </div>
      )}
    </div>
  );
}
